import React from 'react'
import { Link, RouteComponentProps, InjectedRouter, PlainRoute } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { PermissionNames, API } from '@getgreenline/homi-shared'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { Button, message, Skeleton } from 'antd'
import { NoPermissionsResult } from '../../../../../components/NoPermissionsResult'
import { AuditDetail } from './components/AuditDetail'
import './index.scss'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { AuditStore } from './EditAuditStore'
import { AuditModels } from '@getgreenline/audits'
import { Typography } from 'antd-v4'

interface Params {
  auditId: string
}

interface Props extends RouteComponentProps<Params, {}> {
  router: InjectedRouter
  route: PlainRoute
}
interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  audit?: AuditModels.CreateInventoryAudit
  auditStore?: AuditStore
  shouldSyncAllRecords: boolean
}

export interface IProductWithBatchTrackStatus {
  sku: string | null
  barcode: string | null
  productId: string
  productName: string
  productUnit: string
  isBatchTracked: boolean
  purchasePrice: number | null
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class EditAudit extends React.Component<Props & InjectedProps, State> {
  company = this.props.currentCompanyStore!.company!

  canViewAudits = this.props.currentUserStore!.hasPermissionAtEntity(
    PermissionNames.CAN_VIEW_AUDITS,
    this.company.id,
  )

  constructor(props: Props) {
    super(props)

    this.state = {
      audit: undefined,
      auditStore: undefined,
      shouldSyncAllRecords: false,
    }
  }

  componentDidMount = async () => {
    if (!this.canViewAudits) {
      return
    }

    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)

    try {
      const auditResponse = await API.getInventoryAuditById(
        this.company.id,
        this.props.params.auditId,
      )

      const audit = new AuditModels.CreateInventoryAudit(auditResponse)

      const auditStore = new AuditStore(
        this.company,
        audit,
        this.props.currentUserStore!.hasPermissionAtEntity(
          PermissionNames.CAN_EDIT_AUDITS,
          this.company.id,
        ),
      )

      auditStore.setEditable()

      this.setState({ audit, auditStore })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error loading the audit')
    }
  }

  routerWillLeave = () => {
    const { auditStore } = this.state

    if (auditStore && auditStore.unSynceRecordExists) {
      this.setState({ shouldSyncAllRecords: true })

      return 'Your work is not saved! Are you sure you want to leave?'
    }
  }

  componentDidUpdate = async () => {
    const { auditStore } = this.state

    if (auditStore && this.state.shouldSyncAllRecords) {
      message.loading({
        content: 'Syncing unsynced products and lots. Please wait',
        key: 'SYNC_ALL_RECORDS',
      })

      try {
        await auditStore.syncAllRecords()

        message.success({
          content:
            'All audit products and lots were successfully synced. You may leave the page now',
          key: 'SYNC_ALL_RECORDS',
          duration: 10,
        })
      } catch (error) {
        message.error({
          content: parseErrorMsg(error) || 'An error occurred while syncing products',
          key: 'SYNC_ALL_RECORDS',
          duration: 10,
        })
      }
    }
  }

  render() {
    const { audit, auditStore } = this.state

    return (
      <>
        {this.canViewAudits ? (
          audit && auditStore ? (
            <AuditDetail audit={audit!} auditStore={auditStore!} />
          ) : (
            <div>
              <Skeleton active className='p-4' />
              <Skeleton active className='p-4' />
              <Skeleton active className='p-4' />
              <Skeleton active className='p-4' />
            </div>
          )
        ) : (
          <NoPermissionsResult
            permissionName={PermissionNames.CAN_VIEW_AUDITS}
            extra={[
              <Link key='back-to-audits' to={Routes.DASHBOARD_INVENTORY_AUDIT(this.company.id)}>
                <Button>Back to audits</Button>
              </Link>,
            ]}
          />
        )}
      </>
    )
  }
}
